import { create } from "zustand";
import {
  billingSessionService,
  BillingSession,
  CreateBillingSessionDto,
  CloseBillingSessionDto,
} from "../services/billing-session.service";

interface BillingSessionState {
  // In-memory active session; POS rehydrates this from the server if HMR or a
  // transient client reset clears it while the counter remains open.
  activeSession: BillingSession | null;
  isLoading: boolean;
  error: string | null;

  // Modal states
  showOpenSessionModal: boolean;
  showCloseSessionModal: boolean;
  showSwitchWarningModal: boolean;

  // Pending switch action (store or counter id to switch to after session close)
  pendingSwitch: {
    type: "store" | "counter";
    id: string;
  } | null;

  // Actions
  setActiveSession: (session: BillingSession | null) => void;
  setShowOpenSessionModal: (show: boolean) => void;
  setShowCloseSessionModal: (show: boolean) => void;
  setShowSwitchWarningModal: (show: boolean) => void;
  setPendingSwitch: (
    pending: { type: "store" | "counter"; id: string } | null,
  ) => void;

  // API Actions
  fetchActiveSession: (
    storeId: string,
    counterId?: string,
  ) => Promise<BillingSession | null>;
  openSession: (data: CreateBillingSessionDto) => Promise<BillingSession>;
  closeSession: (
    sessionId: string,
    data: CloseBillingSessionDto,
  ) => Promise<BillingSession>;
  clearSession: () => void;
}

export const useBillingSessionStore = create<BillingSessionState>((set) => ({
  activeSession: null,
  isLoading: false,
  error: null,

  showOpenSessionModal: false,
  showCloseSessionModal: false,
  showSwitchWarningModal: false,
  pendingSwitch: null,

  setActiveSession: (session) => set({ activeSession: session }),

  setShowOpenSessionModal: (show) => set({ showOpenSessionModal: show }),

  setShowCloseSessionModal: (show) => set({ showCloseSessionModal: show }),

  setShowSwitchWarningModal: (show) => set({ showSwitchWarningModal: show }),

  setPendingSwitch: (pending) => set({ pendingSwitch: pending }),

  fetchActiveSession: async (storeId: string, counterId?: string) => {
    set({ isLoading: true, error: null });
    try {
      const session = await billingSessionService.getActiveSession(
        storeId,
        counterId,
      );
      set({ activeSession: session, isLoading: false });
      return session;
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      return null;
    }
  },

  openSession: async (data: CreateBillingSessionDto) => {
    set({ isLoading: true, error: null });
    try {
      const session = await billingSessionService.openSession(data);
      set({
        activeSession: session,
        isLoading: false,
        showOpenSessionModal: false,
      });
      return session;
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  closeSession: async (sessionId: string, data: CloseBillingSessionDto) => {
    set({ isLoading: true, error: null });
    try {
      const session = await billingSessionService.closeSession(sessionId, data);
      set({
        activeSession: null,
        isLoading: false,
        showCloseSessionModal: false,
        showSwitchWarningModal: false,
      });
      return session;
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  clearSession: () => {
    set({
      activeSession: null,
      error: null,
      pendingSwitch: null,
    });
  },
}));

// Hook for checking if billing is allowed
export function useCanBill() {
  const activeSession = useBillingSessionStore((state) => state.activeSession);
  return {
    canBill: !!activeSession,
    session: activeSession,
    sessionId: activeSession?.id,
    counterId: activeSession?.counterId,
    counterName: activeSession?.counter?.name,
  };
}
